import { InterviewDraft } from "../types/Interview";

export interface AlteracoesDoRascunho {
  cenario: number;
  checklist: number;
  regras: number;
  regrasCamera2: number;
}

const camposDoCenario: Array<keyof InterviewDraft["scenario"]> = [
  "complexity",
  "environment",
  "sex",
  "age",
  "time_duration",
  "goal_scene",
  "description_scene",
  "illness",
  "allergy",
  "habits",
  "surgeries",
  "family_health_history",
  "continuous_use_medications",
  "temporary_medications",
  "health_guidelines",
  "physical_exam",
  "patient_disclosure",
  "actor",
  "actor_instructions",
  "scenario_evaluated",
  "materials",
];

function mesmoValor(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}

function porRef<T extends { ref: string }>(valores: T[]): Map<string, T> {
  const indice = new Map<string, T>();
  valores.forEach((valor) => indice.set(valor.ref, valor));
  return indice;
}


function contarEntidadesAlteradas<T extends { ref: string }>(
  anteriores: T[],
  atuais: T[],
  mudou: (anterior: T, atual: T) => boolean
): number {
  const antes = porRef(anteriores);
  const depois = porRef(atuais);
  const refs = new Set([...Array.from(antes.keys()), ...Array.from(depois.keys())]);
  let total = 0;

  refs.forEach((ref) => {
    const anterior = antes.get(ref);
    const atual = depois.get(ref);
    if (!anterior || !atual || mudou(anterior, atual)) total += 1;
  });

  return total;
}

/**
 * Conta unidades revisáveis que uma resposta da IA mudou em cada aba.
 * Identidade estável (`ref`) evita confundir reordenação com conteúdo novo.
 */
export default function alteracoesDoRascunho(
  anterior: InterviewDraft,
  atual: InterviewDraft
): AlteracoesDoRascunho {
  const camposRaiz: Array<keyof InterviewDraft> = [
    "name",
    "theme_id",
    "competence_id",
    "instructions",
  ];

  const cenario =
    camposRaiz.reduce(
      (total, campo) => total + (mesmoValor(anterior[campo], atual[campo]) ? 0 : 1),
      0
    ) +
    camposDoCenario.reduce(
      (total, campo) =>
        total + (mesmoValor(anterior.scenario[campo], atual.scenario[campo]) ? 0 : 1),
      0
    );

  const itensAnteriores = anterior.checklist.skills.flatMap((skill) => skill.items);
  const itensAtuais = atual.checklist.skills.flatMap((skill) => skill.items);
  const checklist =
    (mesmoValor(anterior.checklist.bands, atual.checklist.bands) ? 0 : 1) +
    contarEntidadesAlteradas(
      anterior.checklist.skills,
      atual.checklist.skills,
      (antes, depois) =>
        antes.name !== depois.name ||
        antes.points !== depois.points ||
        antes.passing_score !== depois.passing_score
    ) +
    contarEntidadesAlteradas(
      itensAnteriores,
      itensAtuais,
      (antes, depois) =>
        antes.question !== depois.question ||
        antes.value !== depois.value ||
        antes.critical !== depois.critical
    );

  const regras = contarEntidadesAlteradas(
    itensAnteriores,
    itensAtuais,
    (antes, depois) => {
      const { camera2: _, ...principalAntes } = antes.detection;
      const { camera2: __, ...principalDepois } = depois.detection;
      return !mesmoValor(principalAntes, principalDepois);
    }
  );

  const temCamera2 = (item: typeof itensAtuais[number]) =>
    Boolean(item.detection.camera2 && Object.values(item.detection.camera2).some((valor) =>
      typeof valor === "string" ? valor.trim() !== "" : Array.isArray(valor) && valor.length > 0
    ));
  const regrasCamera2 = contarEntidadesAlteradas(
    itensAnteriores.filter(temCamera2),
    itensAtuais.filter(temCamera2),
    (antes, depois) => !mesmoValor(antes.detection.camera2, depois.detection.camera2)
  ) + (mesmoValor(anterior.checklist.camera2_guidance ?? "", atual.checklist.camera2_guidance ?? "") ? 0 : 1);

  return { cenario, checklist, regras, regrasCamera2 };
}
